import { useEffect, useState } from "react";
import Button from "./button.jsx";
import Loading from "./loading.jsx";
import socket from "../functions/socket.js";
import createCookie from "../functions/cookie.js";
import { useNavigate } from "react-router-dom";

const Game = ({game, potentialError}) => {

    const [ nextQuestion, setNextQuestion ] = useState(false);
    const [ gameState, setGameState ] = useState(game);
    const [ loading, setLoading ] = useState(game.title !== "" ? false : true);
    const [ hostName, setHostName ] = useState({
        active: false,
        name: "",
    });

    const handleInput = (field, value) => {
        setHostName(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const joinGame = () => {

        if (socket.connected) {

            if (hostName.name === "") {
                setHostName({
                    active: true,
                    name: ""
                });

                return;
            }

            const user_cookie = createCookie();

            socket.emit("join_game", { code: gameState.code, player: hostName.name, user_cookie: user_cookie });

            return;
        };

        if (!hostName.active) {

            setHostName({
                active: true,
                name: ""
            });

            return;
        }

        if (hostName.active && hostName.name === "") {
            setStartWarning("Bitte gib einen gültigen Spitznamen an!");
            return;
        }
  
        if(!socket.connected) {

            setHostName({
                active: false,
                name: "",
            });

            setLoading(true);

            const user_cookie = createCookie();

            socket.connect();
            socket.once("connect", () => {
                socket.emit("join_game", { code: gameState.code, player: hostName.name, user_cookie: user_cookie });
            });

        }

    };

    const [ rejoin, setRejoin ] = useState(false);
    const [ inGame, setInGame ] = useState(false);

    useEffect(() => {

        const user_cookie = createCookie();

        const active_user = gameState.players.find(p => p.user_cookie === user_cookie);
        
        if (active_user) {
            setHostName({ active: true, name: active_user.name });
            setRejoin(true);

            if(!socket.connected) {
                setLoading(true);
                socket.connect();
                socket.once("connect", () => {
                    socket.emit("join_game", {
                        code: gameState.code,
                        player: active_user.name,
                        user_cookie: user_cookie
                    });
                });
            }
        };

    }, []);

    const [ answer, setAnswer ] = useState(null);

    const [ finished, setFinished ] = useState(false);

    useEffect(() => {

        const onUpdate = (data) => {
            setNextQuestion(false);
            setLoading(false);
            setRejoin(false);
            setStartWarning("");
            setHostName({
                active: false,
                name: "",
            });
            
            setGameState(prev => ({
                ...prev,
                host: data.host,
                players: data.players
            }));
        };

        const onError = (err) => {
            setNextQuestion(false);
            setLoading(false);
            setRejoin(false);

            setHostName({
                active: true,
                name: "",
            });

            setStartWarning(err.error);
        };

        const onStart = (data) => {
            setInGame(true);
            setGameState(data);
            setAnswer({
                given: false,
                text: "",
            });
        };

        const onRejoin = (data) => {
            setLoading(false);
            setInGame(true);
            setGameState(data);
            setNextQuestion(false);

            const active_player = data.players.find(p => p.user_cookie === createCookie());

            if (active_player) {
                if (active_player.answer) {
                    setAnswer({
                        given: true,
                        text: "",
                    });
                    setLoading(true);
                } else {
                    setAnswer({
                        given: false,
                        text: "",
                    });
                    setLoading(false);
                }
            }

        };

        const onAnswer = (data) => {
            setGameState(data);
        };

        const onQuestion = (data) => {
            setLoading(false);
            setNextQuestion(false);
            setAnswer({
                given: false,
                text: "",
            });
            setGameState(data);
        };

        const onFinish = (data) => {
            setFinished(true);
        };

        socket.on("join_error", onError);

        socket.on("join_success", onUpdate);

        socket.on("start", onStart);

        socket.on("rejoin_game", onRejoin);

        socket.on("answer", onAnswer);

        socket.on("new_question", onQuestion);

        socket.on("end_quiz", onFinish);

        return () => {
            socket.off("join_error", onError);
            socket.off("join_success", onUpdate);
        };

    }, []);

    const [ startWarning, setStartWarning ] = useState(potentialError ? "Dieses Quiz existiert nicht mehr!" : "");

    const startGame = () => {

        if(gameState.players.length < 2) {
            setStartWarning("Mindestens zwei Spieler:innen müssen bereit sein!");
            return;
        } else if(!startWarning && (gameState.players.length < gameState.max_players)) {
            const diff = gameState.max_players - gameState.players.length;
            setStartWarning(`${diff} Spieler:innen fehlen noch! Möchtest du trotzdem starten?`);
            return;
        }

        setStartWarning("");

        const user_cookie = createCookie();

        socket.emit("start_game", {
            code: gameState.code,
            user_cookie: user_cookie
        });

    };

    const sendAnswer = (answer) => {

        setAnswer({
            given: true,
            text: answer
        });

        const user_cookie = createCookie();

        setTimeout(() => {

            setLoading(true);

            socket.emit("send_answer", {
                code: gameState.code,
                user_answer: answer,
                user_cookie: user_cookie
            });

        }, 1000);

    };

    const currentPlayer = gameState.players.find(p => p.user_cookie === createCookie());
    const isAdmin = createCookie() === gameState.host_cookie;

    const navigate = useNavigate();

    const handleBack = () => {
        navigate("/quiz");
    };

    const forceNextQuestion = () => {
        const user_cookie = createCookie();
        socket.emit("force_question", {
            code: gameState.code,
            user_cookie: user_cookie
        });
    };

    if (finished) {

        const leaderboard = [...gameState.players].sort((a, b) => b.score - a.score);

        return(
            <div style={{width: "100%", maxWidth: "500px"}}>
                <h2>ERGEBNISSE</h2>

                <div className="leaderboard" style={{ marginTop: "20px" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr style={{ borderBottom: "2px solid var(--blue)" }}>
                                <th style={{ textAlign: "left", padding: "10px" }}>Rang</th>
                                <th style={{ textAlign: "left", padding: "10px" }}>Spieler</th>
                                <th style={{ textAlign: "right", padding: "10px" }}>Punkte</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leaderboard.map((player, index) => (
                                <tr 
                                    key={index} 
                                    style={{ 
                                        background: player.user_cookie === createCookie() ? "rgba(231, 193, 116, 0.2)" : "transparent",
                                    }}
                                >
                                    <td style={{ padding: "10px" }}>{index + 1}.</td>
                                    <td style={{ padding: "10px" }}>
                                        {player.name} {player.user_cookie === createCookie() && " (Du)"}
                                    </td>
                                    <td style={{ padding: "10px", textAlign: "right", fontWeight: "bold" }}>
                                        {player.score}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div onClick={handleBack}>
                    <Button className="button-blue" text="Zurück" />
                </div>
            </div>
        );
    }

    return (
        <div className="flex-col" style={{width: "100%", maxWidth: "500px"}}>

            {loading && <Loading />}

            {startWarning &&
                <div className="start-warning">
                    <p>{startWarning}</p>
                </div>
            }

            {(!potentialError && !inGame) &&
                <div style={{width: "100%"}}>
                    {(!hostName.active && !loading) &&
                        <>
                            <h1 className="title title-small">{gameState.title}</h1>

                            <h2 style={{marginTop: "20px", marginBottom: "5px"}}>Players</h2>

                            {(Array.isArray(gameState.players) && gameState.players.length > 0) ? 
                                (
                                    <ul style={{border: "2px solid var(--blue)", borderRadius: "5px", padding: "10px", width: "70vw", maxWidth: "500px"}}>
                                        {gameState.players.map((player, index) => {

                                            return (
                                                <li key={index} className="flex-row" style={{justifyContent: "flex-start"}}>
                                                    <span style={{fontSize: "1.3rem"}}>{`${index + 1}.`}</span>
                                                    <p style={{fontSize: "1.3rem"}}>{player.name} {player.user_cookie === createCookie() && <span style={{fontSize: "1rem"}}>(du)</span>}</p>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) :

                                (
                                    <p>Derzeit ist dein Quiz-Raum noch leer!</p>
                                )
                            }

                            <p style={{marginTop: "10px", width: "70vw", maxWidth: "500px"}}>Lade weitere Personen ein, indem du die URL mit ihnen teilst!</p>

                            <h2 style={{marginTop: "20px", marginBottom: "5px"}}>Einstellungen</h2>

                            <div className="flex-row" style={{justifyContent: "flex-start"}}>
                                <p>Timer: </p>
                                <p>{game.timer || "ohne"}</p>
                            </div>

                            <div className="flex-row" style={{justifyContent: "flex-start"}}>
                                <p>Anzahl: </p>
                                {Array.isArray(gameState.players) && <p>{`${gameState.players.length}/${gameState.max_players}`}</p>}
                            </div>
                        </>
                    }

                    {(hostName.active && !loading) &&
                        <div style={{fontSize: "2rem", color: "var(--blue)"}}>
                            <label>Spitzname: </label>

                            <input 
                            type="name"
                            style={{borderBottom: "2px solid var(--yellow)", fontSize: "1.3rem"}}
                            value={hostName.name || ""}
                            onChange={(e) => handleInput("name", e.target.value)}
                            />
                        </div>
                    }

                    {!loading &&
                        <>
                            {(Array.isArray(gameState.players) && !gameState.players.some(p => p.user_cookie === createCookie()) || hostName.active) && <div style={{marginTop: "20px", width: "min-content"}} onClick={joinGame}><Button className="button-blue" text={rejoin ? "Rejoin" : "Teilnehmen"} background={hostName.active ? "var(--blue)" : null} color={hostName.active ? "var(--white)" : null} /></div>}
                
                            {(Array.isArray(gameState.players) && !hostName.active && gameState.host === createCookie()) && <div onClick={startGame} style={{width: "min-content"}}><Button className="button-yellow" text="Starten" background={(gameState.players.length < gameState.max_players && !startWarning.includes("trotzdem starten")) ? "rgba(231, 193, 116, 0.6)" : ""} /></div>}
                        </>
                    }
                </div>
            }

            {inGame &&
                <>

                    {!loading &&
                        <>
                            <h2>{gameState.questions[gameState.current_question].sentence}</h2>

                            <p style={{marginTop: "30px"}}>{gameState.questions[gameState.current_question].question_text}</p>

                            <div className="answer-grid">

                                {gameState.questions[gameState.current_question].options.map((option, index) => {

                                    let bg;
                                    if (option === answer.text) {
                                        if (answer.given === gameState.questions[gameState.current_question].answer || gameState.questions[gameState.current_question].answer.includes(option)) {
                                            bg = "green";
                                        } else {
                                            bg = "red"
                                        }
                                    }

                                    if (answer.given && (option === gameState.questions[gameState.current_question].answer || gameState.questions[gameState.current_question].answer.includes(option))) {
                                        bg = "green";
                                    }

                                        return (
                                            <div 
                                            key={index} 
                                            onClick={!answer.given ? () => sendAnswer(option) : null}
                                            style={{background: bg}}
                                            >
                                                <p>{option}</p>
                                            </div>
                                        );
                                    })}

                            </div>

                            <div className="flex-row row-start" style={{marginTop: "20px"}}>

                                <p>Punkte:</p>

                                <p>{currentPlayer.score}</p>

                            </div>

                        </>
                    }

                    {loading &&
                        <>
                            <p style={{textAlign: "center"}}>Warte auf andere Spieler:innen...</p>

                            {isAdmin &&
                                <div className="quiz-next-question" onClick={() => setNextQuestion(true)}>
                                    <Button className="button-yellow" text="Nächste Frage" />
                                </div>
                            }

                            {nextQuestion &&
                                <div className="confirmation" style={{flexDirection: "column", alignItems: "flex-start", padding: "20px"}}>
                                    <p>Bist du dir sicher, dass du zur nächsten Frage wechseln möchtest? Es haben {gameState.players.filter(a => a.answer).length} von {gameState.players.length} Spielern geantwortet.</p>

                                    <div className="flex-row">

                                        <div onClick={() => setNextQuestion(false)}>
                                            <Button className="button-blue" text="Nein" />
                                        </div>

                                        <div onClick={forceNextQuestion}>
                                            <Button className="button-yellow" text="Ja" />
                                        </div>

                                    </div>
                                </div>
                            }
                        </>
                    }
                    
                </>
            }

        </div>
    );

}; export default Game;